'use client';

import type { ListProduct } from '@/api/types/public';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type RefObject,
  type UIEvent,
} from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { CapsuleModelMedia, CapsuleRuntimeData, CapsuleStyleItem } from '../../../types';
import { useCapsuleStyleProducts } from '../hooks/useCapsuleStyleProducts';
import { getCapsuleStyleVenueId, resolveCapsuleAssetUrl } from '../utils';
import styles from './CapsuleStyleVenue.module.css';
import ProductCard from './ProductCard';

interface CapsuleStyleVenueProps {
  adp?: string;
  data: CapsuleRuntimeData;
  isPreview: boolean;
  isWap: boolean;
  marketingPageId: number;
  onProductCollect?: CapsuleProductCollectHandler;
  onProductClick?: CapsuleProductTrackingHandler;
  onProductExposure?: CapsuleProductExposureHandler;
}

type CapsuleProductTrackingHandler = (product: ListProduct, index: number, tabName: string) => void;

type CapsuleProductCollectHandler = (
  product: ListProduct,
  index: number,
  collectType: string,
) => void;

type CapsuleProductExposureHandler = CapsuleProductTrackingHandler;

const noopProductExposure: CapsuleProductExposureHandler = () => undefined;
const noopProductClick: CapsuleProductTrackingHandler = () => undefined;
const noopProductCollect: CapsuleProductCollectHandler = () => undefined;

interface CapsuleStyleVenueItemProps {
  hasMore: boolean;
  index: number;
  isLoadingMore: boolean;
  item: CapsuleStyleItem;
  onLoadMore: () => void;
  onProductCollect: CapsuleProductCollectHandler;
  onProductClick: CapsuleProductTrackingHandler;
  onProductExposure: CapsuleProductExposureHandler;
  products: ListProduct[];
}

function getProductName(product: ListProduct): string {
  return product.short_name || product.name || product.shop_sku || '';
}

function getProductHref(product: ListProduct): string {
  return product.shop_sku ? `/goods-detail/${product.shop_sku}` : '';
}

function CapsuleWapModelCard({
  item,
  model,
}: {
  item: CapsuleStyleItem;
  model: CapsuleModelMedia;
}) {
  const toJumpTarget = useJump();
  const image = resolveCapsuleAssetUrl(model.image);

  if (!image) return null;

  const handleClick = () => {
    if (!model.sku_id) return;
    toJumpTarget({ jump_type: 4, Jump_target: `/goods-detail/${model.sku_id}` });
  };

  return (
    <button
      type="button"
      disabled={!model.sku_id}
      aria-label={model.sku_id ? model.alt || item.title : undefined}
      className={`relative block aspect-[351/160] w-full overflow-hidden rounded-[4px] ${model.sku_id ? 'cursor-pointer' : 'cursor-default'}`}
      onClick={handleClick}
      data-capsule-wap-model
    >
      <Image
        src={image}
        alt={model.alt || item.title || ''}
        fill
        sizes="calc(100vw - 24px)"
        className="object-cover object-center"
      />
    </button>
  );
}

function CapsuleWapSceneCard({
  item,
  image,
  product,
  productIndex,
  onProductClick,
}: {
  item: CapsuleStyleItem;
  image: string;
  product?: ListProduct;
  productIndex: number;
  onProductClick: (index: number, product: ListProduct) => void;
}) {
  const toJumpTarget = useJump();
  const href = product ? getProductHref(product) : '';
  const productName = product ? getProductName(product) : '';

  const handleClick = () => {
    if (!product || !href) return;

    onProductClick(productIndex, product);
    toJumpTarget({ jump_type: 4, Jump_target: href });
  };

  return (
    <button
      type="button"
      disabled={!href}
      aria-label={href ? productName || item.scene?.alt || item.title : undefined}
      className={`relative block h-[187px] w-full overflow-hidden ${href ? 'cursor-pointer' : 'cursor-default'}`}
      onClick={handleClick}
      data-capsule-wap-scene
    >
      <Image
        src={image}
        alt={item.scene?.alt || item.title || ''}
        fill
        sizes="calc((100vw - 24px) / 2)"
        className="object-cover object-center"
      />
    </button>
  );
}

function CapsuleWapProductCard({
  product,
  index,
  onProductCollect,
  onProductClick,
  onValidExposure,
  compact = false,
}: {
  product: ListProduct;
  index: number;
  onProductCollect: (index: number, product: ListProduct, collectType: string) => void;
  onProductClick: (index: number, product: ListProduct) => void;
  onValidExposure: (index: number, product: ListProduct) => void;
  compact?: boolean;
}) {
  return (
    <ProductCard
      product={product}
      index={index + 1}
      handelClick={onProductClick}
      onValidExposure={onValidExposure}
      wished={() => onProductCollect(index + 1, product, '收藏')}
      removed={() => onProductCollect(index + 1, product, '取消收藏')}
      className={`min-w-0 overflow-hidden bg-[#eeeeee] ${compact ? 'h-[187px]' : 'rounded-[4px]'}`}
      imageClassName={`${compact ? '!h-[100px]' : '!h-[129px]'} !aspect-auto !min-h-0 !rounded-none`}
      showTryOn={false}
      showMember={false}
      showTag={false}
      showName={false}
      namePriceInline={false}
      showCorrelation
      showCorrelationCount={3}
      wapCardVariant={compact ? 'capsule-compact' : 'capsule'}
      collectScene="胶囊活动页移动端风格版位"
      loginSituation="胶囊活动页移动端风格版位收藏"
    />
  );
}

function CapsuleStyleVenueWapItem({
  hasMore,
  index,
  isLoadingMore,
  item,
  onLoadMore,
  onProductCollect,
  onProductClick,
  onProductExposure,
  products,
}: CapsuleStyleVenueItemProps) {
  const [activePage, setActivePage] = useState(0);
  const firstProduct = products[0];
  const sceneImage =
    resolveCapsuleAssetUrl(item.scene?.wap_image) ||
    resolveCapsuleAssetUrl(firstProduct?.image?.material?.[0]) ||
    resolveCapsuleAssetUrl(firstProduct?.image?.celebrity?.[0]);
  const model = item.models?.wap;
  const modelImage = resolveCapsuleAssetUrl(model?.image);
  const firstPageProductCount = sceneImage ? 3 : 4;
  const firstPageProducts = products.slice(0, firstPageProductCount);
  const remainingProducts = products.slice(firstPageProductCount);
  const laterPages = Array.from(
    { length: Math.ceil(remainingProducts.length / 4) },
    (_, pageIndex) => remainingProducts.slice(pageIndex * 4, pageIndex * 4 + 4),
  );
  const hasFirstPage = Boolean(sceneImage || firstPageProducts.length > 0);
  const pageCount = (hasFirstPage ? 1 : 0) + laterPages.length;
  const showProgress = products.length > 3 && pageCount > 1;
  const progressOffset = pageCount > 1 ? (activePage / (pageCount - 1)) * 90 : 0;
  const carouselId = `capsule-wap-style-products-${index}`;
  const handleProductExposure = useCallback(
    (productIndex: number, product: ListProduct) => {
      onProductExposure(product, productIndex, item.title || '');
    },
    [item.title, onProductExposure],
  );
  const handleProductClick = useCallback(
    (productIndex: number, product: ListProduct) => {
      onProductClick(product, productIndex, item.title || '');
    },
    [item.title, onProductClick],
  );
  const handleProductCollect = useCallback(
    (productIndex: number, product: ListProduct, collectType: string) => {
      onProductCollect(product, productIndex, collectType);
    },
    [onProductCollect],
  );

  if (!modelImage && !hasFirstPage) return null;

  return (
    <article
      id={getCapsuleStyleVenueId(index)}
      className="flex w-full scroll-mt-[100px] flex-col"
      data-capsule-style-venue-wap-item
    >
      {item.title || item.sub_title ? (
        <header
          className="mb-[16px] flex w-full flex-col items-center gap-[8px] text-center text-primary-text"
          data-capsule-wap-style-heading
        >
          {item.title ? (
            <h2 className="font-be text-[24px] font-extrabold leading-normal text-[#282828]">
              {item.title}
            </h2>
          ) : null}
          {item.sub_title ? (
            <p className="w-full max-w-[349px] font-br text-[12px] leading-normal text-primary-text">
              {item.sub_title}
            </p>
          ) : null}
        </header>
      ) : null}

      {model && modelImage ? <CapsuleWapModelCard item={item} model={model} /> : null}

      {hasFirstPage ? (
        <div className={`${modelImage ? 'mt-[8px]' : ''} w-full`}>
          <Swiper
            id={carouselId}
            autoHeight
            slidesPerView={1}
            slidesPerGroup={1}
            spaceBetween={12}
            loop={pageCount > 1}
            className={`w-full ${styles.wapProductSwiper}`}
            onSlideChange={(swiper) => {
              setActivePage(swiper.realIndex);
              if (swiper.realIndex === pageCount - 1 && hasMore && !isLoadingMore) {
                onLoadMore();
              }
            }}
          >
            <SwiperSlide>
              <div className="flex w-full flex-col gap-[8px]" data-capsule-wap-product-page="0">
                {sceneImage ? (
                  <>
                    <div className="flex w-full overflow-hidden rounded-[4px]">
                      <div className="w-1/2 shrink-0">
                        <CapsuleWapSceneCard
                          item={item}
                          image={sceneImage}
                          product={firstPageProducts[0]}
                          productIndex={1}
                          onProductClick={handleProductClick}
                        />
                      </div>
                      {firstPageProducts[0] ? (
                        <div className="w-1/2 shrink-0">
                          <CapsuleWapProductCard
                            product={firstPageProducts[0]}
                            index={0}
                            onProductCollect={handleProductCollect}
                            onProductClick={handleProductClick}
                            onValidExposure={handleProductExposure}
                            compact
                          />
                        </div>
                      ) : null}
                    </div>
                    {firstPageProducts.length > 1 ? (
                      <div className="grid w-full grid-cols-2 gap-x-[7px]">
                        {firstPageProducts.slice(1).map((product, productIndex) => (
                          <CapsuleWapProductCard
                            key={`${product.id || product.shop_sku}-${productIndex + 1}`}
                            product={product}
                            index={productIndex + 1}
                            onProductCollect={handleProductCollect}
                            onProductClick={handleProductClick}
                            onValidExposure={handleProductExposure}
                          />
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="grid w-full grid-cols-2 gap-x-[7px] gap-y-[8px]">
                    {firstPageProducts.map((product, productIndex) => (
                      <CapsuleWapProductCard
                        key={`${product.id || product.shop_sku}-${productIndex}`}
                        product={product}
                        index={productIndex}
                        onProductCollect={handleProductCollect}
                        onProductClick={handleProductClick}
                        onValidExposure={handleProductExposure}
                      />
                    ))}
                  </div>
                )}
              </div>
            </SwiperSlide>
            {laterPages.map((pageProducts, pageIndex) => (
              <SwiperSlide key={`page-${pageIndex + 1}`}>
                <div
                  className="grid w-full grid-cols-2 gap-x-[7px] gap-y-[8px]"
                  data-capsule-wap-product-page={pageIndex + 1}
                >
                  {pageProducts.map((product, productIndex) => {
                    const absoluteProductIndex =
                      firstPageProductCount + pageIndex * 4 + productIndex;

                    return (
                      <CapsuleWapProductCard
                        key={`${product.id || product.shop_sku}-${absoluteProductIndex}`}
                        product={product}
                        index={absoluteProductIndex}
                        onProductCollect={handleProductCollect}
                        onProductClick={handleProductClick}
                        onValidExposure={handleProductExposure}
                      />
                    );
                  })}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {showProgress ? (
            <div
              aria-hidden="true"
              className="relative mx-auto mt-[28px] h-[2px] w-[120px] overflow-hidden rounded-[2px] bg-[#eeeeee]"
              data-capsule-wap-products-progress
            >
              <span
                className="absolute inset-y-0 left-0 w-[30px] rounded-[4px] bg-secondary-text transition-transform duration-300"
                style={{ transform: `translateX(${progressOffset}px)` }}
                data-capsule-wap-products-progress-thumb
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function CapsuleFeaturedProduct({
  product,
  index,
  onProductClick,
  onValidExposure,
}: {
  product: ListProduct;
  index: number;
  onProductClick: (index: number, product: ListProduct) => void;
  onValidExposure: (index: number, product: ListProduct) => void;
}) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const toJumpTarget = useJump();
  const href = getProductHref(product);
  const productPrice = priceInfo(userInfo, product, currency) as {
    price?: string;
    sell_price?: string;
  };
  const priceText = productPrice.sell_price
    ? formatPriceNumberWithSymbol(productPrice.sell_price, currency)
    : '';
  const originalPriceText = productPrice.price
    ? formatPriceNumberWithSymbol(productPrice.price, currency)
    : '';
  const showOriginalPrice = Boolean(originalPriceText && originalPriceText !== priceText);
  const image = resolveCapsuleAssetUrl(product.image?.product?.[0]);
  const name = getProductName(product);
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExposure(index, product),
    ts: product.shop_sku || `${product.id || index}`,
  });

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!href || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    onProductClick(index, product);
    toJumpTarget({ jump_type: 4, Jump_target: href });
  };

  return (
    <a
      ref={validExposureRef}
      href={href || undefined}
      aria-label={name}
      className="box-border flex w-[276px] shrink-0 flex-col overflow-hidden rounded-[6px] bg-white p-[16px] text-primary-text min-[1280px]:w-[352px] min-[1280px]:rounded-[8px] min-[1280px]:p-[20px] min-[1920px]:w-[528px] min-[1920px]:rounded-[12px] min-[1920px]:p-[24px]"
      onClick={handleClick}
    >
      <div className="relative h-[121px] w-full overflow-hidden rounded-[6px] bg-white min-[1280px]:h-[155px] min-[1280px]:rounded-[8px] min-[1920px]:h-[240px]">
        {image ? (
          <Image
            src={image}
            alt={name}
            fill
            sizes="(min-width: 1920px) 480px, (min-width: 1280px) 312px, 244px"
            className="object-contain"
          />
        ) : null}
      </div>
      <div className="flex min-h-[56px] items-end justify-between gap-[8px] px-[6px] py-[5px] min-[1280px]:min-h-[74px] min-[1280px]:px-[8px] min-[1280px]:py-[8px] min-[1920px]:min-h-[119px] min-[1920px]:gap-[16px] min-[1920px]:px-[16px] min-[1920px]:py-[12px]">
        <div className="min-w-0">
          <p className="truncate font-br text-[16px] leading-normal min-[1280px]:text-[20px] min-[1920px]:text-[32px]">
            {name}
          </p>
          <div className="mt-[8px] flex items-center gap-[8px] whitespace-nowrap text-[16px] leading-normal min-[1280px]:text-[20px] min-[1920px]:mt-[12px] min-[1920px]:text-[32px]">
            <span className="font-bb font-bold">{priceText}</span>
            {showOriginalPrice ? (
              <span className="font-br text-secondary-text line-through">{originalPriceText}</span>
            ) : null}
          </div>
        </div>
        <span
          aria-hidden="true"
          className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary-text min-[1280px]:size-[24px] min-[1920px]:size-[40px]"
          data-featured-product-action
        >
          <span
            className="size-[12px] bg-[url('/zeelool-next/activity/capsule/share.svg')] bg-contain bg-center bg-no-repeat min-[1280px]:size-[16px] min-[1920px]:size-[24px]"
            data-featured-product-action-icon
          />
        </span>
      </div>
    </a>
  );
}

function CapsuleModelSwiper({
  item,
  onWheel,
}: {
  item: CapsuleStyleItem;
  onWheel: (event: globalThis.WheelEvent) => void;
}) {
  const toJumpTarget = useJump();
  const modelContainerRef = useRef<HTMLDivElement>(null);
  const models = item.models?.pc || [];

  useEffect(() => {
    const modelContainer = modelContainerRef.current;
    if (!modelContainer) return;

    modelContainer.addEventListener('wheel', onWheel, { passive: false });
    return () => modelContainer.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  if (models.length === 0) return null;

  const handleModelClick = (skuId?: string) => {
    if (!skuId) return;
    toJumpTarget({ jump_type: 4, Jump_target: `/goods-detail/${skuId}` });
  };

  return (
    <div
      ref={modelContainerRef}
      className="h-full w-[35.85%] shrink-0 overflow-hidden rounded-[8px]"
      data-capsule-model-scroll-proxy
    >
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={
          models.length > 1
            ? {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        pagination={models.length > 1 ? { clickable: true } : false}
        loop={models.length > 1}
        className={`h-full w-full ${styles.modelSwiper}`}
      >
        {models.map((model, modelIndex) => {
          const image = resolveCapsuleAssetUrl(model.image);
          if (!image) return null;

          return (
            <SwiperSlide key={`${model.image}-${model.sku_id || modelIndex}`}>
              <button
                type="button"
                disabled={!model.sku_id}
                aria-label={model.sku_id ? model.alt || item.title : undefined}
                className={`relative block h-full w-full overflow-hidden ${model.sku_id ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => handleModelClick(model.sku_id)}
              >
                <Image
                  src={image}
                  alt={model.alt || item.title || ''}
                  fill
                  sizes="36vw"
                  className="object-cover object-center"
                />
              </button>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}

function CapsuleProductGrid({
  hasMore,
  isLoadingMore,
  onLoadMore,
  products,
  onProductCollect,
  onProductClick,
  onValidExposure,
  scrollerRef,
}: {
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  products: ListProduct[];
  onProductCollect: (index: number, product: ListProduct, collectType: string) => void;
  onProductClick: (index: number, product: ListProduct) => void;
  onValidExposure: (index: number, product: ListProduct) => void;
  scrollerRef: RefObject<HTMLDivElement | null>;
}) {
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!hasMore || isLoadingMore) return;

      const container = event.currentTarget;
      const distanceToBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToBottom <= 8) onLoadMore();
    },
    [hasMore, isLoadingMore, onLoadMore],
  );

  if (products.length === 0) return null;

  return (
    <div
      ref={scrollerRef}
      className={`h-full min-w-0 flex-1 overflow-y-auto ${styles.productScroller}`}
      onScroll={handleScroll}
      aria-busy={isLoadingMore}
      data-capsule-product-grid-scroll
    >
      <div className="grid grid-cols-2 gap-[16px] pr-[2px]">
        {products.map((product, index) => (
          <ProductCard
            key={`${product.id || product.shop_sku}-${index}`}
            product={product}
            index={index + 1}
            handelClick={onProductClick}
            onValidExposure={onValidExposure}
            wished={() => onProductCollect(index + 1, product, '收藏')}
            removed={() => onProductCollect(index + 1, product, '取消收藏')}
            className="min-w-0 overflow-hidden rounded-[8px] bg-[#eeeeee]"
            imageClassName="!aspect-[4/3] !min-h-0 !rounded-t-[8px]"
            productInfoClassName="!px-[24px] !pb-[16px] !pt-[12px]"
            showTryOn={false}
            showMember={false}
            showTag={false}
            showName={false}
            namePriceInline={false}
            showCorrelation
            showCorrelationCount={4}
            collectScene="胶囊活动页风格版位"
            loginSituation="胶囊活动页风格版位收藏"
          />
        ))}
      </div>
    </div>
  );
}

function CapsuleStyleVenueItem({
  hasMore,
  index,
  isLoadingMore,
  item,
  onLoadMore,
  onProductCollect,
  onProductClick,
  onProductExposure,
  products,
}: CapsuleStyleVenueItemProps) {
  const productScrollerRef = useRef<HTMLDivElement>(null);
  const sceneImage = resolveCapsuleAssetUrl(item.scene?.pc_image);
  const featuredProduct = products[0];
  const gridProducts = products.slice(1);
  const mediaFirst = index % 2 === 0;
  const handleProductExposure = useCallback(
    (productIndex: number, product: ListProduct) => {
      onProductExposure(product, productIndex, item.title || '');
    },
    [item.title, onProductExposure],
  );
  const handleProductClick = useCallback(
    (productIndex: number, product: ListProduct) => {
      onProductClick(product, productIndex, item.title || '');
    },
    [item.title, onProductClick],
  );
  const handleProductCollect = useCallback(
    (productIndex: number, product: ListProduct, collectType: string) => {
      onProductCollect(product, productIndex, collectType);
    },
    [onProductCollect],
  );
  const handleModelWheel = useCallback((event: globalThis.WheelEvent) => {
    const scroller = productScrollerRef.current;
    if (!scroller || event.deltaY === 0) return;

    const maxScrollTop = Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
    const canScroll = event.deltaY > 0 ? scroller.scrollTop < maxScrollTop : scroller.scrollTop > 0;
    if (!canScroll) return;

    const scrollDistance =
      event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * scroller.clientHeight
          : event.deltaY;

    event.preventDefault();
    scroller.scrollTop = Math.min(Math.max(scroller.scrollTop + scrollDistance, 0), maxScrollTop);
  }, []);
  const modelSwiper = <CapsuleModelSwiper item={item} onWheel={handleModelWheel} />;
  const productGrid = (
    <CapsuleProductGrid
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
      products={gridProducts}
      onProductCollect={handleProductCollect}
      onProductClick={handleProductClick}
      onValidExposure={handleProductExposure}
      scrollerRef={productScrollerRef}
    />
  );

  if (!sceneImage && (item.models?.pc.length || 0) === 0 && products.length === 0) return null;

  return (
    <article
      id={getCapsuleStyleVenueId(index)}
      className="flex w-full scroll-mt-[100px] flex-col gap-[16px]"
    >
      {sceneImage ? (
        <div className="relative aspect-[1824/500] min-h-[254px] w-full overflow-hidden rounded-[6px] min-[1280px]:min-h-0 min-[1280px]:rounded-[8px]">
          <Image
            src={sceneImage}
            alt={item.scene?.alt || item.title || ''}
            fill
            sizes="calc(100vw - 96px)"
            className="object-cover object-center"
          />
          <div
            className={`absolute inset-y-0 left-[24px] right-[24px] flex items-center justify-between gap-[32px] py-[22px] min-[1280px]:left-[32px] min-[1280px]:right-[32px] min-[1280px]:py-[28px] min-[1920px]:left-[55px] min-[1920px]:right-[55px] min-[1920px]:py-0 ${mediaFirst ? '' : 'flex-row-reverse'}`}
          >
            <div
              className={`w-[240px] shrink-0 text-white min-[1920px]:w-[574px] ${mediaFirst ? 'text-left' : 'text-right'}`}
            >
              <h2 className="font-be text-[20px] font-extrabold leading-normal min-[1280px]:text-[24px] min-[1920px]:text-[40px]">
                {item.title}
              </h2>
              {item.sub_title ? (
                <p className="mt-[8px] font-br text-[12px] leading-normal min-[1280px]:text-[14px] min-[1920px]:mt-[10px] min-[1920px]:text-[24px]">
                  {item.sub_title}
                </p>
              ) : null}
            </div>
            {featuredProduct ? (
              <CapsuleFeaturedProduct
                product={featuredProduct}
                index={0}
                onProductClick={handleProductClick}
                onValidExposure={handleProductExposure}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex aspect-[1824/872] w-full gap-[16px] overflow-hidden">
        {mediaFirst ? modelSwiper : productGrid}
        {mediaFirst ? productGrid : modelSwiper}
      </div>
    </article>
  );
}

export default function CapsuleStyleVenue({
  data,
  marketingPageId,
  isPreview,
  isWap,
  adp,
  onProductCollect = noopProductCollect,
  onProductClick = noopProductClick,
  onProductExposure = noopProductExposure,
}: CapsuleStyleVenueProps) {
  const groupIds = useMemo(
    () => data.style_list.flatMap((item) => (item.product_group_id ? [item.product_group_id] : [])),
    [data.style_list],
  );
  const { hasMoreByGroup, loadingMoreByGroup, loadMoreProducts, productsByGroup } =
    useCapsuleStyleProducts({
      groupIds,
      marketingPageId,
      isPreview,
      adp,
      soldOutDisplay: data.sold_out_display,
      soldOutSort: data.sold_out_sort,
    });

  if (data.style_list.length === 0) return null;

  if (isWap) {
    return (
      <section className="flex w-full flex-col gap-[40px] px-[12px] py-[40px]">
        {data.style_list.map((item, index) => {
          const groupId = item.product_group_id;
          return (
            <CapsuleStyleVenueWapItem
              key={`${groupId || 'style'}-${item.title || index}`}
              hasMore={groupId ? hasMoreByGroup[groupId] || false : false}
              index={index}
              isLoadingMore={groupId ? loadingMoreByGroup[groupId] || false : false}
              item={item}
              onLoadMore={() => {
                if (groupId) void loadMoreProducts(groupId);
              }}
              onProductCollect={onProductCollect}
              onProductClick={onProductClick}
              onProductExposure={onProductExposure}
              products={groupId ? productsByGroup[groupId] || [] : []}
            />
          );
        })}
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-[calc(100%_-_96px)] max-w-[1824px] flex-col gap-[40px] py-[40px] min-[1025px]:gap-[60px] min-[1025px]:py-[60px] min-[1281px]:gap-[70px] min-[1281px]:py-[70px] min-[1441px]:gap-[80px] min-[1441px]:py-[80px]">
      {data.style_list.map((item, index) => {
        const groupId = item.product_group_id;
        return (
          <CapsuleStyleVenueItem
            key={`${groupId || 'style'}-${item.title || index}`}
            hasMore={groupId ? hasMoreByGroup[groupId] || false : false}
            index={index}
            isLoadingMore={groupId ? loadingMoreByGroup[groupId] || false : false}
            item={item}
            onLoadMore={() => {
              if (groupId) void loadMoreProducts(groupId);
            }}
            onProductCollect={onProductCollect}
            onProductClick={onProductClick}
            onProductExposure={onProductExposure}
            products={groupId ? productsByGroup[groupId] || [] : []}
          />
        );
      })}
    </section>
  );
}
